import { type RefObject, useCallback, useLayoutEffect, useRef } from "react";

const DEFAULT_BOTTOM_THRESHOLD_PX = 24;

interface UseAutoScrollToLatestOptions {
  bottomThresholdPx?: number;
}

interface MessageScrollItem {
  id: string;
  content: string;
  role?: string;
}

function isNearBottom(element: HTMLElement, thresholdPx: number): boolean {
  const distanceFromBottom =
    element.scrollHeight - element.scrollTop - element.clientHeight;
  return distanceFromBottom <= thresholdPx;
}

export function latestMessageScrollKey(
  messages: readonly MessageScrollItem[],
  trailingKey: string | number | boolean = "",
): string {
  let hash = 0;
  for (const message of messages) {
    hash = appendHash(hash, message.id);
    hash = appendHash(hash, message.role ?? "");
    hash = appendHash(hash, message.content);
  }
  return [messages.length, hash.toString(36), String(trailingKey)].join(":");
}

function appendHash(seed: number, value: string): number {
  let hash = seed;
  for (let i = 0; i < value.length; i++) {
    hash = Math.imul(hash ^ value.charCodeAt(i), 16777619);
  }
  return hash >>> 0;
}

export function useAutoScrollToLatest<T extends HTMLElement>(
  scrollRef: RefObject<T | null>,
  scrollKey: string,
  options: UseAutoScrollToLatestOptions = {},
) {
  const bottomThresholdPx =
    options.bottomThresholdPx ?? DEFAULT_BOTTOM_THRESHOLD_PX;
  const shouldStickToBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    shouldStickToBottomRef.current = isNearBottom(element, bottomThresholdPx);
  }, [bottomThresholdPx, scrollRef]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      shouldStickToBottomRef.current = true;
      return;
    }
    if (
      !shouldStickToBottomRef.current &&
      !isNearBottom(element, bottomThresholdPx)
    ) {
      return;
    }
    element.scrollTop = element.scrollHeight;
    shouldStickToBottomRef.current = true;
  }, [bottomThresholdPx, scrollKey, scrollRef]);

  return { onScroll: handleScroll };
}
